<script setup lang="ts">
import { VisDonut } from "@unovis/vue";
import { Donut } from "@unovis/ts";
import { eventBus } from "#bb/lib/eventBus";
import { computed, provide } from "vue";
import { ChartTooltip, ChartTooltipContent, componentToString, type ChartConfig } from "~/components/ui/chart";

const props = defineProps<{ id: string }>();

const { getContentById } = useContentManager();
const definitionData = computed(() => getContentById(props.id));

const commands = [
  {
    id: "editors-group",
    type: "group",
    name: "Editors",
    description: "Donut editors",
    commands: [
      {
        id: "edit",
        name: "Edit",
        description: "Edit this donut chart",
        action: async () => {
          // @ts-ignore - dynamic event name not in mitt typed Events
          eventBus.emit(`component:onEdit:${props.id}`, {
            data: definitionData.value,
          });
        },
        icon: "lucide:edit",
      },
    ],
  },
];

defineExpose({
  commands,
  properties: {
    id: props.id,
    name: "Donut Chart",
    icon: "lucide:chart-pie",
  },
});

// consume the same provided report data with lifecycle flags
type InjectedReport = { data: any[]; loading?: boolean; initialized?: boolean };
const report = inject<InjectedReport>("report", {
  data: [],
  loading: false,
  initialized: false,
});
const chartData = computed(() => (report.loading || !report.initialized ? [] : report.data));

// Interactive visibility toggle via legend
const hiddenKeys = reactive<Record<string, boolean>>({});
const itemKey = (d: any, i: number) =>
  typeof d === "object" && (d.key ?? d.id ?? d.name)
    ? String(d.key ?? d.id ?? d.name)
    : String(i);
const filteredData = computed(() => {
  const data = Array.isArray(chartData.value) ? chartData.value : [];
  return data.filter((d, i) => !hiddenKeys[itemKey(d, i)]);
});

// Compute the series keys either from settings or auto-detect
const seriesKeys = computed(() => {
  const configSeries = definitionData.value?.series;
  if (Array.isArray(configSeries) && configSeries.length > 0) {
    return configSeries;
  }
  const firstRow = report.data[0];
  if (firstRow) {
    return Object.keys(firstRow).filter((key) => key !== "name");
  }
  return [];
});

// index-based x accessor; y accessors derived from the series keys
const x = (_d: any, i: number) => i;
const y = computed(() => {
  return seriesKeys.value.map((key) => {
    return (row: any) => row?.[key] ?? 0;
  });
});

const dataStep = computed(() => definitionData.value?.dataStep ?? 1);
const orientation = computed(
  () => definitionData.value?.orientation ?? "vertical"
);
// Make the orientation available to axes via inject
provide("xy-orientation", orientation);

const colorAccessorCode = computed(
  () => definitionData.value?.colorAccessor ?? "(d, i) => null"
);

const color = computed(() => {
  try {
    return eval(colorAccessorCode.value);
  } catch (err) {
    console.error("Error evaluating color accessor:", err);
    return () => null;
  }
});
// Legend: labels, colors, values, percentages
const showLegend = computed(() => definitionData.value?.showLegend ?? true);

// Robust value accessor (already defined) + helpers for totals/percents
const totalValue = computed(() =>
  (Array.isArray(filteredData.value) ? filteredData.value : []).reduce(
    (acc, d) => acc + valueAccessor(d),
    0
  )
);
const formatPercent = (v: number) => {
  const t = totalValue.value;
  if (!t) return "0%";
  return `${Math.round((v / t) * 100)}%`;
};

const legendItems = computed(() => {
  const c = color.value;
  const data = Array.isArray(chartData.value) ? chartData.value : [];
  return data.map((d: any, i: number) => {
    const key = itemKey(d, i);
    const label = typeof d === "object"
      ? d.key ?? d.name ?? String(d.id ?? i + 1)
      : String(d);
    const value = valueAccessor(d);
    const percent = formatPercent(value);
    return {
      key,
      label,
      value,
      percent,
      color: c ? c(d, i) : undefined,
      hidden: !!hiddenKeys[key],
    };
  });
});

function toggleLegendItem(key: string) {
  hiddenKeys[key] = !hiddenKeys[key];
}

// Tooltip config built from data + color accessor
const tooltipConfig = computed<ChartConfig>(() => {
  const cfg: ChartConfig = {};
  const data = Array.isArray(chartData.value) ? chartData.value : [];
  const colorFn = color.value;

  data.forEach((d: any, i: number) => {
    const key = itemKey(d, i);
    const label = typeof d === "object"
      ? d.key ?? d.name ?? String(d.id ?? i + 1)
      : String(d);
    const colorValue = colorFn ? colorFn(d, i) : undefined;
    cfg[key] = {
      label,
      ...(colorValue ? { color: colorValue } : {}),
    };
  });

  return cfg;
});

const tooltipTemplate = computed(() =>
  componentToString(tooltipConfig.value, ChartTooltipContent)
);

const tooltipTriggers = computed(() => {
  return {
    [Donut.selectors.segment]: (e: any) => {
      const data = e?.data ?? e;
      const list = Array.isArray(chartData.value) ? chartData.value : [];
      const index = list.findIndex((item) =>
        item === data || item?.key === data?.key || item?.id === data?.id || item?.name === data?.name
      );
      const key = itemKey(data, index >= 0 ? index : 0);
      const colorFn = color.value;
      const payload = {
        [key]: valueAccessor(data),
        fill: colorFn ? colorFn(data, index >= 0 ? index : 0) : undefined,
      } as any;

      return tooltipTemplate.value ? tooltipTemplate.value(payload as any, undefined as any) : "";
    },
  } as any;
});

// Donut-specific configurable props with sensible defaults
const showEmptySegments = computed(
  () => definitionData.value?.showEmptySegments ?? true
);
const padAngle = computed(() => definitionData.value?.padAngle ?? 0.03);
const arcWidth = computed(() => definitionData.value?.arcWidth ?? 50);
const centralLabel = computed(() => definitionData.value?.centralLabel ?? "");
const centralSubLabel = computed(
  () => definitionData.value?.centralSubLabel ?? ""
);
const centralLabelOffsetX = computed(
  () => definitionData.value?.centralLabelOffsetX ?? 0
);
const centralLabelOffsetY = computed(
  () => definitionData.value?.centralLabelOffsetY ?? 0
);
const angleRange = computed(() =>
  Array.isArray(definitionData.value?.angleRange) &&
    definitionData.value?.angleRange?.length === 2
    ? definitionData.value?.angleRange
    : [0, Math.PI * 2]
);
const radius = computed(() => definitionData.value?.radius ?? 50);
const cornerRadius = computed(() => definitionData.value?.cornerRadius ?? 0);
const showBackground = computed(
  () => definitionData.value?.showBackground ?? true
);

// Robust value accessor that supports primitives and common object shapes
const valueAccessor = (d: any): number => {
  if (d == null) return 0;
  if (typeof d === "number") return d;
  if (typeof d === "object") {
    const v = (d as any).value ?? (d as any).score ?? (d as any).amount ?? 0;
    return typeof v === "number" ? v : Number(v) || 0;
  }
  const n = Number(d);
  return isNaN(n) ? 0 : n;
};

</script>

<template>
  <div>
    <VisDonut :value="valueAccessor" :data="filteredData" :color="color" :showEmptySegments="showEmptySegments"
      :padAngle="padAngle" :arcWidth="arcWidth" :centralLabel="centralLabel" :centralSubLabel="centralSubLabel"
      :centralLabelOffsetX="centralLabelOffsetX" :centralLabelOffsetY="centralLabelOffsetY" :angleRange="angleRange"
      :radius="radius" :cornerRadius="cornerRadius" :showBackground="showBackground" />
    <ChartTooltip :triggers="tooltipTriggers" />
  </div>
</template>

<script lang="ts">
import { ComponentDefinitionIds } from "#bb/lib/componentDefinitionIds";
export default {
  editor: () => import("#bb/components/editor/unovis/single/donut.vue"),
  componentId: ComponentDefinitionIds.UNOVIS_SINGLE_DONUT,
  id: "xydonut",
  icon: "lucide:chart-pie",
  type: "Donut Chart",
  description:
    "Donut Chart is a Unovis component for displaying data in a donut format.",
  default: {
    columnsSettings: { default: 1 },
    series: [],
    dataStep: 1,
    orientation: "vertical",
    colorAccessor:
      "(d) => { \n  const val = typeof d === 'object' ? d.score : d;\n  return val < 35 ? '#fb2c36' :\n         val < 50 ? '#fe9a00' :\n         '#00bc7d';\n}",
    showLegend: true,
    // Donut-specific defaults
    showEmptySegments: true,
    padAngle: 0.03,
    arcWidth: 50,
    centralLabel: "",
    centralSubLabel: "",
    centralLabelOffsetX: 0,
    centralLabelOffsetY: 0,
    angleRange: [0, Math.PI * 2],
    radius: 50,
    cornerRadius: 0,
    showBackground: true,
  },
  categories: ["UNOVIS"],
  order: 12004,
};
</script>
